import PropTypes from "prop-types";
import React from "react";
import { Card, Col, Row, Accordion } from "react-bootstrap";
import "./AnalysisResult.css";

const AnalysisResult = ({ result }) => {
  if (!result) {
    return <p className="text-danger">Aucun résultat disponible.</p>;
  }

  const {
    note_etat_piece = "Non spécifiée",
    type_renovation_generale = "Non spécifiée",
    localisation_projet = "Non spécifiée",
    surface_habitable_totale_logement_m2 = 0,
    surface_piece_a_renover_m2 = 0,
    age_logement_annees = 0,
    surface_vitree_totale_proposee_m2 = 0,
    conformite_surface_vitree_re2020 = false,
    cout_total_estime_ht = 0,
    cout_total_imprevus_ht = 0,
    commentaires_generaux = "",
    corps_de_metier = [],
  } = result || {};

  const safeNumber = (n) => (typeof n === "number" && !isNaN(n) ? n : 0);
  const formatEUR = (n) => (Number.isFinite(n) ? n : 0).toLocaleString("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

  const currentYear = new Date().getFullYear();

  const computeAgeYears = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n) || n <= 0) return 0;
    // If we receive a year (e.g., 1998), convert to age; if it's already an age (e.g., 25), keep it.
    if (n > 1900 && n <= currentYear + 1) return Math.max(0, currentYear - n);
    if (n < 200) return n;
    return 0;
  };

  const totalMaterialsHT = corps_de_metier.reduce((sum, metier) => {
    const subtotal = (metier?.travaux_a_effectuer || []).reduce(
      (s, t) => s + safeNumber(t?.cout_total_materiaux_moyen_ht),
      0
    );
    return sum + subtotal;
  }, 0);

  const totalLaborHT = corps_de_metier.reduce((sum, metier) => {
    const subtotal = (metier?.travaux_a_effectuer || []).reduce(
      (s, t) => s + safeNumber(t?.cout_total_main_oeuvre_moyen_ht),
      0
    );
    return sum + subtotal;
  }, 0);

  const breakdownHT = totalMaterialsHT + totalLaborHT;
  const totalWithImprevusHT = breakdownHT + safeNumber(cout_total_imprevus_ht);

  const getMetierTotalHT = (metier) =>
    (metier?.travaux_a_effectuer || []).reduce(
      (s, t) => s + safeNumber(t?.cout_total_travail_moyen_ht),
      0
    );

  return (
    <div className="analysis-result-container">
      <div className="analysis-title-wrapper">
        <h4 className="analysis-main-title">Synthèse de l’analyse</h4>
        {typeof note_etat_piece === 'number' && !isNaN(note_etat_piece) && (
            <div className="text-end">
                <div className="kpi-label">Note d'état</div>
                <div className="note-display">{note_etat_piece}/10</div>
            </div>
        )}
      </div>

      <Card className="summary-card mb-4 budget-highlight">
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <div className="kpi-label">Total estimé HT (incl. imprévus)</div>
              <div className="kpi-value">
                {formatEUR(totalWithImprevusHT || cout_total_estime_ht)}
              </div>
              {safeNumber(cout_total_imprevus_ht) > 0 && (
                <div className="kpi-subtext">
                  Dont imprévus: {formatEUR(cout_total_imprevus_ht)}
                </div>
              )}
            </Col>
            <Col md="auto">
              <div className="kpi-split">
                <div>
                  <span>Matériaux</span>
                  <strong>{formatEUR(totalMaterialsHT)}</strong>
                </div>
                <div>
                  <span>Main d’œuvre</span>
                  <strong>{formatEUR(totalLaborHT)}</strong>
                </div>
              </div>
            </Col>
          </Row>
          <div className="section-note mt-2">Montants affichés HT. TVA non incluse.</div>
        </Card.Body>
      </Card>

      <Card className="summary-card mb-4">
        <Card.Body>
          <Row>
            <Col md={3} sm={6} xs={12} className="mb-3">
              <div className="kpi-card">
                <div className="kpi-label">Type de rénovation</div>
                <div className="kpi-value-small">{type_renovation_generale}</div>
              </div>
            </Col>
            <Col md={3} sm={6} xs={12} className="mb-3">
              <div className="kpi-card">
                <div className="kpi-label">Surface à rénover</div>
                <div className="kpi-value-small">
                  {safeNumber(surface_piece_a_renover_m2).toLocaleString("fr-FR", {
                    maximumFractionDigits: 1,
                  })}
                  
                  m²
                </div>
              </div>
            </Col>
            <Col md={3} sm={6} xs={12} className="mb-3">
              <div className="kpi-card">
                <div className="kpi-label">Localisation</div>
                <div className="kpi-value-small">{localisation_projet}</div>
              </div>
            </Col>
            <Col md={3} sm={6} xs={12} className="mb-3">
              <div className="kpi-card">
                <div className="kpi-label">Âge du logement</div>
                <div className="kpi-value-small">{computeAgeYears(age_logement_annees)} ans</div>
              </div>
            </Col>
          </Row>
          {safeNumber(surface_habitable_totale_logement_m2) > 0 && (
            <Row>
              <Col md={3} sm={6} xs={12} className="mb-2">
                <div className="kpi-card">
                  <div className="kpi-label">Surface habitable</div>
                  <div className="kpi-value-small">
                    {safeNumber(surface_habitable_totale_logement_m2).toLocaleString(
                      "fr-FR",
                      { maximumFractionDigits: 1 }
                    )}
                    
                    m²
                  </div>
                </div>
              </Col>
            </Row>
          )}
          {safeNumber(surface_vitree_totale_proposee_m2) > 0 && (
            <div className="section-note mt-2">
              Surface vitrée proposée: {safeNumber(surface_vitree_totale_proposee_m2).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} m² • Conformité RE2020: {conformite_surface_vitree_re2020 ? "Oui" : "Non"}
            </div>
          )}
          {commentaires_generaux && (
            <div className="section-note mt-2">
              <i className="fas fa-comment-dots"></i> {commentaires_generaux}
            </div>
          )}
        </Card.Body>
      </Card>

      <h5 className="analysis-sub-title">Détail par corps de métier</h5>
      {corps_de_metier?.length ? (
        <Accordion alwaysOpen>
          {corps_de_metier.map((metier, index) => {
            const metierTotal = getMetierTotalHT(metier);
            return (
              <Accordion.Item eventKey={String(index)} key={index} className="metier-card mb-3">
                <Accordion.Header>
                  <span className={`fas ${getMetierIcon(metier?.nom_metier)} icon-header`}></span>
                  <span className="me-2">{metier?.nom_metier}</span>
                  <span className="accordion-total">{formatEUR(metierTotal)}</span>
                </Accordion.Header>
                <Accordion.Body>
                  {(metier?.travaux_a_effectuer || []).length ? (
                    (metier.travaux_a_effectuer || []).map((travail, idx) => {
                      if (!travail) return null;
                      const totalTravail = safeNumber(travail.cout_total_travail_moyen_ht);
                      const totalMateriaux = safeNumber(travail.cout_total_materiaux_moyen_ht);
                      const totalMainOeuvre = safeNumber(travail.cout_total_main_oeuvre_moyen_ht);
                      return (
                        <div key={idx} className="travail-item mb-2">
                          <div className="travail-description">{travail.description_travail}</div>
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="text-muted">
                              {safeNumber(travail.quantite)} {travail.unite_mesure}
                            </div>
                            <div className="travail-amount-column">
                              <div className="line"><span className="text-muted">Matériaux</span><strong className="travail-amount">{formatEUR(totalMateriaux)}</strong></div>
                              <div className="line"><span className="text-muted">Main d’œuvre</span><strong className="travail-amount">{formatEUR(totalMainOeuvre)}</strong></div>
                            </div>
                          </div>
                          {travail.commentaires_specifiques && (
                            <div className="text-muted small mt-1">
                              {travail.commentaires_specifiques}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="mb-0">Aucun travail spécifié pour ce corps de métier.</p>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : (
        <p>Aucun corps de métier spécifié.</p>
      )}
    </div>
  );
};

const getMetierIcon = (metier) => {
  switch (metier) {
    case "Électricité": return "fa-bolt";
    case "Menuiserie": return "fa-hammer";
    case "Plomberie": return "fa-faucet";
    case "Gros œuvre": return "fa-hard-hat";
    case "Maçonnerie": return "fa-brick";
    case "Peinture": return "fa-paint-roller";
    case "Plâtrerie": return "fa-fill-drip";
    case "Carrelage": return "fa-th-large";
    case "Chauffage": return "fa-fire";
    case "Couverture": return "fa-house-chimney";
    case "Isolation": return "fa-thermometer-half";
    case "Serrurerie": return "fa-key";
    case "Charpente": return "fa-gopuram";
    case "Terrassement": return "fa-shovel";
    case "Cuisiniste": return "fa-kitchen-set";
    case "Démolition": return "fa-bomb";
    case "Évacuation Gravats": return "fa-dumpster";
    case "Négoce": return "fa-handshake";
    default: return "fa-tools";
  }
};

AnalysisResult.propTypes = {
  result: PropTypes.shape({
    note_etat_piece: PropTypes.number,
    type_renovation_generale: PropTypes.string,
    localisation_projet: PropTypes.string,
    surface_habitable_totale_logement_m2: PropTypes.number,
    surface_piece_a_renover_m2: PropTypes.number,
    age_logement_annees: PropTypes.number,
    surface_vitree_totale_proposee_m2: PropTypes.number,
    conformite_surface_vitree_re2020: PropTypes.bool,
    cout_total_estime_ht: PropTypes.number,
    cout_total_imprevus_ht: PropTypes.number,
    commentaires_generaux: PropTypes.string,
    corps_de_metier: PropTypes.arrayOf(
      PropTypes.shape({
        nom_metier: PropTypes.string,
        travaux_a_effectuer: PropTypes.arrayOf(
          PropTypes.shape({
            description_travail: PropTypes.string,
            unite_mesure: PropTypes.string,
            quantite: PropTypes.number,
            cout_total_travail_moyen_ht: PropTypes.number,
            cout_total_materiaux_moyen_ht: PropTypes.number,
            cout_total_main_oeuvre_moyen_ht: PropTypes.number,
          })
        ),
      })
    ),
  }),
};

export default AnalysisResult;
